import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { payersApi, type PayerDirectoryPatch } from '../api/payersApi';
import { payerKeys } from '../queryKeys';

// Agency-only editing of the InsurancePayer directory entry itself
// (aliases, provider line, promoting pending → active, etc.) — distinct from
// a location's InsuranceLocationPayer selection/override.
export function usePayerDirectoryEntry(payerId: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: payerKeys.directoryEntry(payerId ?? ''),
    queryFn: () => payersApi.getPayerDirectoryEntry(payerId as string),
    enabled: !!payerId,
  });

  const update = useMutation({
    mutationFn: (patch: PayerDirectoryPatch) =>
      payersApi.updatePayerDirectoryEntry(payerId as string, patch),
    onSuccess: () => {
      if (payerId) queryClient.invalidateQueries({ queryKey: payerKeys.directoryEntry(payerId) });
      // Prefix matches (no locationId/query) — refreshes the practice Payers
      // page list (payerName/providerLine are joined from this entry) and
      // the agency Command Center's full-directory browse page.
      queryClient.invalidateQueries({ queryKey: payerKeys.listPrefix() });
      queryClient.invalidateQueries({ queryKey: payerKeys.directoryAll() });
    },
  });

  return { ...query, entry: query.data ?? null, update };
}
